import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CommonHeader from '../../util_components/common_header';
import Button from '../../util_components/button';
import CustomInput from '../../util_components/custom_input';
import CustomSelect from '../../util_components/custom_single_select';
import { get_api_url } from '../../../utils/urls';
import { api_with_method } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';
import './cms_users.css';

const initialAddForm = { name: '', email: '', access_type: '', department_uuid: '' };

export default function CmsUsers() {
  const access_pages = useSelector((state) => state.home.access_pages);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [accessTypes, setAccessTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedUuid, setSelectedUuid] = useState('');
  const [addForm, setAddForm] = useState(initialAddForm);
  const [editForm, setEditForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // access driven by cms_page_access rows (SUPER_USER / HR) via get_accessible_pages
  const has_access = (access_pages || []).includes('/cms-users');

  // access types come from the cms_user.access_type SET definition (served by
  // the list endpoint), so the dropdown always matches what the column accepts
  const access_type_options = accessTypes.map((opt) => ({ value: opt, label: opt }));

  const department_options = departments.map((d) => ({ value: d.uuid, label: d.name }));

  const notify = (type, message) =>
    dispatch(actions.set_notification_variable(true, type, message));

  const error_message = (e) => {
    try {
      return e.response.data.message || e.response.data.reason;
    } catch (err) {
      return 'Some error occured. Please contact dev team.';
    }
  };

  const load_users = () => {
    setLoading(true);
    const url = get_api_url('/v2/cms_user/manage/users', false, true);
    return api_with_method('get', url, null, true)
      .then((res) => {
        const loaded_users = res.data.data.users || [];
        setUsers(loaded_users);
        setDepartments(res.data.data.departments || []);
        setAccessTypes(res.data.data.access_types || []);
        setLoading(false);
        return loaded_users;
      })
      .catch((e) => {
        notify('error', error_message(e));
        setLoading(false);
        return [];
      });
  };

  useEffect(() => {
    if (has_access) load_users();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const select_user = (user) => {
    setSelectedUuid(user.uuid);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
      access_type: user.access_type || '',
      department_uuid: user.department_uuid || '',
      is_active: user.is_active
    });
  };

  const open_add = () => {
    setSelectedUuid('');
    setEditForm(null);
    setAddForm(initialAddForm);
  };

  const on_add_change = (key) => (e) => setAddForm({ ...addForm, [key]: e.target.value });

  const on_edit_change = (key) => (e) => setEditForm({ ...editForm, [key]: e.target.value });

  const submit_add = () => {
    const { name, email, access_type, department_uuid } = addForm;
    if (!name || !email || !access_type) {
      notify('error', 'Name, email and access type are required.');
      return;
    }
    setSubmitting(true);
    // Password is set server-side (random UUID) — sign-in is via Google.
    const url = get_api_url('/v2/cms_user/manage/users', false, true);
    api_with_method('post', url, { name, email, access_type, department_uuid }, true)
      .then(() => load_users())
      .then(() => {
        notify('success', 'User added successfully.');
        setSubmitting(false);
        setAddForm(initialAddForm);
      })
      .catch((e) => {
        notify('error', error_message(e));
        setSubmitting(false);
      });
  };

  const submit_edit = (overrides = {}) => {
    const payload = { ...editForm, ...overrides };
    if (!payload.name || !payload.access_type) {
      notify('error', 'Name and access type are required.');
      return;
    }
    setSubmitting(true);
    const url = get_api_url(`/v2/cms_user/manage/users/${selectedUuid}`, false, true);
    api_with_method('put', url, payload, true)
      .then(() => {
        notify('success', 'User updated successfully.');
        setSubmitting(false);
        setEditForm(payload);
        load_users();
      })
      .catch((e) => {
        notify('error', error_message(e));
        setSubmitting(false);
      });
  };

  const filtered_users = users.filter((u) => {
    if ((u.access_type || '').trim() === 'NO_ACCESS') return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.access_type || '').toLowerCase().includes(q) ||
      (u.department_name || '').toLowerCase().includes(q)
    );
  });

  const render_list = () => (
    <div className="cms-card flex-1 min-w-0 rounded-xl overflow-hidden p-5">
      <div className="cms-list-header flex items-center gap-3 pb-4">
        <input
          className="cms-search-input rounded px-3 py-1.5 w-full text-sm"
          placeholder="Search by name, email, role or department"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="cms-users-count text-sm whitespace-nowrap">
          {filtered_users.length} users
        </div>
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
        <table className="cms-users-table w-full text-sm">
          <thead>
            <tr>
              <th className="p-3 font-medium text-left">Name</th>
              <th className="p-3 font-medium text-left">Email</th>
              <th className="p-3 font-medium text-left">Role</th>
              <th className="p-3 font-medium text-left">Department</th>
              <th className="p-3 font-medium text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered_users.map((user) => (
              <tr key={user.uuid} className={user.uuid === selectedUuid ? 'is-selected' : ''}>
                <td className="p-3 text-left">{user.name}</td>
                <td className="p-3 text-left break-all">{user.email}</td>
                <td className="p-3 text-left">{user.access_type}</td>
                <td className="p-3 text-left">{user.department_name || '-'}</td>
                <td className="p-3 text-left">
                  <button
                    type="button"
                    data-tooltip-id="my-tooltip"
                    data-tooltip-content="Edit user"
                    className="cms-edit-btn cursor-pointer"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      boxShadow: 'none',
                      padding: 0,
                      margin: 0
                    }}
                    onClick={() => select_user(user)}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M16.5 3.5a2.121 2.121 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
            {filtered_users.length === 0 && !loading ? (
              <tr>
                <td className="cms-empty p-4 text-left" colSpan={5}>
                  No users found
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );

  const render_add_form = () => (
    <>
      <div className="text-lg font-semibold mb-4">Add User</div>
      <div className="mb-3">
        <CustomInput
          label="Name"
          name="name"
          value={addForm.name}
          onChange={on_add_change('name')}
          style={{ width: '100%' }}
        />
      </div>
      <div className="mb-3">
        <CustomInput
          label="Email"
          name="email"
          type="email"
          value={addForm.email}
          onChange={on_add_change('email')}
          style={{ width: '100%' }}
        />
      </div>
      <div className="mb-4">
        <CustomSelect
          label="Access Type"
          name="access_type"
          value={addForm.access_type}
          options={access_type_options}
          onChange={on_add_change('access_type')}
          defaultOption="Select access type"
          style={{ width: '100%' }}
        />
      </div>
      <div className="mb-4">
        <CustomSelect
          label="Department"
          name="department_uuid"
          value={addForm.department_uuid}
          options={department_options}
          onChange={on_add_change('department_uuid')}
          defaultOption="Select department (optional)"
          style={{ width: '100%' }}
        />
      </div>
      <Button onClick={submit_add} disabled={submitting}>
        {submitting ? 'Submitting...' : 'Add User'}
      </Button>
    </>
  );

  const render_edit_form = () => (
    <>
      <div className="mb-4">
        <div className="text-lg font-semibold">Edit User</div>
      </div>
      <div className="mb-3">
        <CustomInput
          label="Name"
          name="name"
          value={editForm.name}
          onChange={on_edit_change('name')}
          style={{ width: '100%' }}
        />
      </div>
      <div className="mb-3">
        <CustomInput
          label="Email"
          name="email"
          value={editForm.email}
          disabled
          style={{ width: '100%' }}
        />
      </div>
      <div className="mb-4">
        <CustomSelect
          label="Access Type"
          name="access_type"
          value={editForm.access_type}
          options={access_type_options}
          onChange={on_edit_change('access_type')}
          defaultOption="Select access type"
          style={{ width: '100%' }}
        />
      </div>
      <div className="mb-4">
        <CustomSelect
          label="Department"
          name="department_uuid"
          value={editForm.department_uuid}
          options={department_options}
          onChange={on_edit_change('department_uuid')}
          defaultOption="Select department (optional)"
          style={{ width: '100%' }}
        />
      </div>
      <div className="flex gap-3">
        <Button type="secondary" onClick={open_add} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={() => submit_edit()} disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </>
  );

  if (!has_access) {
    return (
      <CommonHeader title="CMS Users" class_name="cms-users-page">
        <div className="cms-no-access p-4">You do not have access to this page.</div>
      </CommonHeader>
    );
  }

  return (
    <CommonHeader loading={loading} title="CMS Users" class_name="cms-users-page">
      <div className="flex gap-6 items-start">
        {render_list()}
        <div className="cms-card w-96 shrink-0 rounded-xl p-5">
          {editForm ? render_edit_form() : render_add_form()}
        </div>
      </div>
    </CommonHeader>
  );
}
